import { v4 as uuidv4 } from 'uuid';
import type { RPCMessage, RPCQuery, RPCResponse } from './RPCMessage';
import type { RPCParams, RPCReturn } from './RPCCommand';
import type { RPCCommandRegistry } from './RPCCommandRegistry';
import Container from 'typedi';
import { Logger } from '../../../schools-backend/src/shared/application/Logger';

const DEFAULT_TIMEOUT_MS = 100_000;

interface OngoingQuery {
    resolve: (response: RPCResponse) => void;
    reject: (error: Error) => void;
    timeout: ReturnType<typeof setTimeout>;
}

/**
 * Bidirectional RPC channel.
 *
 * - TOutgoing: command map for outgoing calls (what this side can *call*).
 * - TIncoming: command map for incoming calls (what this side *handles*).
 * - TContext:  optional context passed as first arg to incoming command handlers.
 */
export class RPCChannel<TOutgoing, TIncoming, TContext = void> {
    private ongoingQueries: Map<string, OngoingQuery> = new Map();
    private send: ((message: RPCMessage) => void | Promise<void>) | null = null;

    constructor(
        private readonly commands: RPCCommandRegistry<TIncoming, TContext>,
        private readonly context?: TContext,
    ) { }

    /** Call a command on the remote side. */
    async call<K extends keyof TOutgoing & string>(
        command: K,
        ...args: RPCParams<TOutgoing, K>
    ): Promise<RPCReturn<TOutgoing, K>> {
        const id = uuidv4();

        const query: RPCQuery = { id, isQuery: true, command, args };

        const response: RPCResponse = await new Promise<RPCResponse>(async (resolve, reject) => {
            if (!this.send) {
                reject(new Error('Not connected'));
                return;
            }

            const timeout = setTimeout(() => {
                this.ongoingQueries.delete(id);
                reject(new Error(`RPC timeout for message ${id}`));
            }, DEFAULT_TIMEOUT_MS);

            this.ongoingQueries.set(id, {
                resolve: (res) => {
                    clearTimeout(timeout);
                    this.ongoingQueries.delete(id);
                    resolve(res);
                },
                reject: (err) => {
                    clearTimeout(timeout);
                    this.ongoingQueries.delete(id);
                    reject(err);
                },
                timeout,
            });

            try {
                await this.send(query);
            } catch (err) {
                this.ongoingQueries.get(id)?.reject(err as Error);
            }
        });

        if (response.error) {
            throw new Error(response.error.message);
        }

        return response.response as RPCReturn<TOutgoing, K>;
    }

    /** Handle an incoming RPC message (query or response). */
    async handleMessage(message: RPCMessage): Promise<void> {
        if (message.isQuery) {
            const command = this.commands.get(message.command);

            const response: RPCResponse = {
                id: message.id,
                isQuery: false,
                response: null,
            };

            try {
                if (!command) {
                    throw new Error(`Unknown command: ${message.command}`);
                }
                const args = message.args as any[];
                response.response = this.context !== undefined
                    ? await (command.run as Function)(this.context, ...args)
                    : await (command.run as Function)(...args);
            } catch (e) {
                response.error = { message: (e as Error).message };
            } finally {
                if (this.send) {
                    await this.send(response);
                }
            }
        } else {
            const ongoing = this.ongoingQueries.get(message.id);
            if (!ongoing) {
                return;
            }
            ongoing.resolve(message);
        }
    }

    start(send: ((message: RPCMessage) => void | Promise<void>) | null): void {
        this.send = send;
    }

    close(): void {
        this.send = null;
        const pending = [...this.ongoingQueries.values()];
        this.ongoingQueries.clear();
        for (const q of pending) {
            clearTimeout(q.timeout);
            q.reject(new Error('Connection closed'));
        }
    }
}
